import { useTranslation } from '@crehana/i18n';
import GoogleIcon from '@crehana/icons/GoogleIcon';
import Microsoft from '@crehana/icons/logos/Microsoft';
import { SimpleErrorBoundary } from '@crehana/layouts';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Tooltip } from '@crehana/ui';

import useFeatureFlagByNameKey from '@/shared/hooks/useFeatureFlagByNameKey/useFeatureFlagByNameKey';
import { AuthenticationProviderEnum } from '@/shared/types/graphql/globals.v5';
import { B2BLoginSocialButtons } from '@/views/Auth/components';

import { useSocialLoginMethods } from '../hooks/useSocialLoginMethods';
import { Divider } from './ui';

const supportedProviders = {
  [AuthenticationProviderEnum.AUTHENTICATION_PROVIDER_GOOGLE]: {
    Icon: GoogleIcon,
    tooltipLabelKey: 'LOGIN_WITH_GOOGLE_SIGN_IN',
    tooltipLabelFallback: 'Ingresar con Google',
    label: 'BUTTON_LABEL_LOGIN_WITH_GOOGLE_SIGN_IN',
  },
  [AuthenticationProviderEnum.AUTHENTICATION_PROVIDER_MICROSOFT]: {
    Icon: Microsoft,
    tooltipLabelKey: 'LOGIN_WITH_MICROSOFT_SIGN_IN',
    tooltipLabelFallback: 'Ingresar con Microsoft',
    label: 'BUTTON_LABEL_LOGIN_WITH_MICROSOFT_SIGN_IN',
  },
  [AuthenticationProviderEnum.AUTHENTICATION_PROVIDER_SECUREAUTH]: {
    imageUrl:
      'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/e956fdcb-b2e8-46ee-90d2-bad95cafcc34-5bfda3fbf2233-(1).png?auto=format&fit=clip&w=56&h=56',
    tooltipLabelKey: 'LOGIN_WITH_MICROSOFT_SECURE_AUTH',
    tooltipLabelFallback: 'Ingresar con SecureAuth',
    label: 'BUTTON_LABEL_LOGIN_WITH_MICROSOFT_SECURE_AUTH',
  },
  [AuthenticationProviderEnum.AUTHENTICATION_PROVIDER_OPENAGORA]: {
    imageUrl:
      'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/2c816c77-2a56-4edc-89b6-92da662375d4-iso.svg?auto=format&fit=clip&w=56&h=56',
    tooltipLabelKey: 'LOGIN_WITH_OPENAGORA',
    tooltipLabelFallback: 'Ingresar con Openagora',
    label: 'BUTTON_LABEL_LOGIN_WITH_OPENAGORA',
  },
} as const;

export function SocialButtonsSection({
  organizationId,
  nextUrl,
}: {
  organizationId: number | null;
  nextUrl: string;
}) {
  const { t } = useTranslation();

  const { loadingSocialLoginMethods, socialLoginMethods } =
    useSocialLoginMethods({ organizationId, nextUrl });

  const { isFlagEnabled: areSocialButtonsEnabled } = useFeatureFlagByNameKey({
    key: 'USER_LOGIN_SSO_BUTTONS',
    organizationId: organizationId,
  });

  if (
    loadingSocialLoginMethods ||
    (!socialLoginMethods.length && !areSocialButtonsEnabled)
  ) {
    return null;
  }

  const supportedMethods = Object.keys(supportedProviders);
  const loginMethods = socialLoginMethods.filter(login =>
    supportedMethods.includes(login.provider),
  );

  if (loginMethods.length === 0) {
    return null;
  }

  const renderLoginButtons = () => {
    if (socialLoginMethods.length === 0) {
      return (
        <B2BLoginSocialButtons
          className="mb-16"
          nextUrl={nextUrl}
          label={t('SOCIAL_LABEL')}
        />
      );
    }

    return (
      <div>
        {/* =================== Desktop ======================= */}
        <div className="tw-hidden md:tw-block">
          <div className="tw-flex tw-items-center tw-mb-1.6">
            <span className="tw-font-subtitle3 tw-mr-2.4">
              {t('SOCIAL_LABEL')}
            </span>
            <div>
              <div className="w-full flex">
                {loginMethods.map(loginMethod => {
                  const {
                    Icon,
                    imageUrl,
                    tooltipLabelKey,
                    tooltipLabelFallback,
                  } =
                    // @ts-expect-error check with the code owner why the ButtonPropertiesByProvider doesn't have all the providers
                    supportedProviders[loginMethod.provider];

                  return (
                    <div key={`login-method-${loginMethod.provider}`}>
                      <Tooltip
                        label={t(tooltipLabelKey, tooltipLabelFallback)}
                        position="bottom"
                        type="regular"
                      >
                        <a
                          className="flex items-center justify-center tw-p-1.2 bg-white tw-rounded-0.8 tw-border tw-border-solid tw-border-neutral-light-400"
                          href={loginMethod.method_url}
                        >
                          {imageUrl && (
                            // legacy code, will be fixed in a future rewrite
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              width={28}
                              height={28}
                              src={imageUrl}
                              alt={t(tooltipLabelKey)}
                            />
                          )}
                          {Icon && <Icon size={28} />}
                        </a>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ========== Mobile =============== */}
        <div className="tw-block md:tw-hidden">
          {loginMethods.map(loginMethod => {
            const { Icon, imageUrl, tooltipLabelKey, label } =
              // @ts-expect-error check with the code owner why the ButtonPropertiesByProvider doesn't have all the providers
              supportedProviders[loginMethod.provider];

            return (
              <a
                key={`login-method-${loginMethod.provider}`}
                href={loginMethod.method_url}
                className="tw-py-0.8 tw-px-2 tw-rounded-0.8 tw-border tw-border-solid tw-border-neutral-light-400 tw-bg-neutral-light-100 tw-flex tw-items-center tw-justify-center tw-gap-0.8"
              >
                <span>
                  {imageUrl && (
                    // legacy code, will be fixed in a future rewrite
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      width={20}
                      height={20}
                      src={imageUrl}
                      alt={tooltipLabelKey}
                    />
                  )}
                  {Icon && <Icon size={20} />}
                </span>
                <span className="tw-font-body3">{t(label)}</span>
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <SimpleErrorBoundary>
      <div className="tw-flex tw-flex-col">
        {renderLoginButtons()}

        <div className="tw-order-first md:tw-order-none tw-my-3.2 md:tw-my-0">
          <Divider />
        </div>
      </div>
    </SimpleErrorBoundary>
  );
}
